import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Plus,
  Search,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  UserCheck,
  UserX,
  RefreshCw,
  Download,
  Edit,
  MoreVertical,
  ArrowUpDown,
  X,
  Loader,
  Save,
  AlertCircle,
  Trash2,
  ArrowLeft,
  Eye,
  EyeOff,
  MessageSquare,
  Clock,
  LifeBuoy
} from 'lucide-react';
import api, { getFileUrl } from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import FileUpload from '../../components/Common/FileUpload';
import * as XLSX from 'xlsx';
import { sanitizeInput } from '../../utils/sanitize';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// Common Components
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import Badge from '../../components/Common/Badge';

const ITSupportManagement = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('management');
  const [allTickets, setAllTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimer = useRef(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAddPassword, setShowAddPassword] = useState(false);
  const [showAddConfirmPassword, setShowAddConfirmPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showEditConfirmPassword, setShowEditConfirmPassword] = useState(false);

  const [editFormData, setEditFormData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    status: 'active',
    qualification: '',
    specialization: '',
    profileImage: ''
  });

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    role: 'support',
    status: 'active',
    qualification: '',
    specialization: '',
    profileImage: ''
  });

  // Debounce search input
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(debounceTimer.current);
  }, [searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [debouncedSearch, statusFilter, currentPage, sortBy, sortOrder]);

  useEffect(() => {
    fetchTickets();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  const fetchTickets = async () => {
    try {
      setLoadingTickets(true);
      const { data } = await api.get('/tickets/admin/all');
      if (data.success) {
        setAllTickets(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch all tickets for analytics:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  const getPersonStats = (supportUserId) => {
    const assigned = allTickets.filter(ticket => {
      if (!ticket.assignedTo) return false;
      const assignedId = typeof ticket.assignedTo === 'object' ? ticket.assignedTo._id : ticket.assignedTo;
      return assignedId === supportUserId;
    });
    const awaiting = assigned.filter(t => t.status === 'open' || t.status === 'in-progress').length;
    const resolved = assigned.filter(t => t.status === 'resolved' || t.status === 'closed').length;
    return {
      total: assigned.length,
      awaiting,
      resolved
    };
  };

  const getCategoryStats = () => {
    const categories = [
      'technical',
      'account',
      'course',
      'assessment',
      'career Direction',
      'placement issue',
      'certificates & badges issue',
      'other'
    ];
    const labelMapping = {
      'technical': 'Technical',
      'account': 'Account',
      'course': 'Course',
      'assessment': 'Assessment',
      'career Direction': 'Career Direction',
      'placement issue': 'Placement',
      'certificates & badges issue': 'Certificates & Badges',
      'other': 'Other'
    };
    const counts = {
      'technical': 0,
      'account': 0,
      'course': 0,
      'assessment': 0,
      'career Direction': 0,
      'placement issue': 0,
      'certificates & badges issue': 0,
      'other': 0
    };
    allTickets.forEach(ticket => {
      const cat = (ticket.category || 'other').toLowerCase();
      if (counts[cat] !== undefined) {
        counts[cat] += 1;
      } else if (cat === 'course & assessment') {
        // Fallback for legacy database records
        counts['course'] += 1;
      } else if (cat.includes('technical')) {
        counts['technical'] += 1;
      } else if (cat.includes('account')) {
        counts['account'] += 1;
      } else if (cat.includes('course')) {
        counts['course'] += 1;
      } else if (cat.includes('assessment')) {
        counts['assessment'] += 1;
      } else if (cat.includes('placement')) {
        counts['placement issue'] += 1;
      } else if (cat.includes('certificate') || cat.includes('badge')) {
        counts['certificates & badges issue'] += 1;
      } else if (cat.includes('career')) {
        counts['career Direction'] += 1;
      } else {
        counts['other'] += 1;
      }
    });
    return categories.map(cat => ({
      name: labelMapping[cat],
      count: counts[cat]
    }));
  };

  const fetchUsers = async () => {
    fetchTickets();
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        role: 'support', // Specifically filter for IT support
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(statusFilter && { status: statusFilter }),
        sortBy,
        sortOrder
      };

      const { data } = await api.get('/users', { params });

      if (data.success) {
        setUsers(data.data);
        setTotalPages(data.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch IT support users:', error);
      toast.error('Failed to fetch IT support users');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    const confirmed = window.confirm(`Are you sure you want to change status to ${newStatus}?`);
    if (!confirmed) return;

    try {
      const { data } = await api.put(`/users/${id}/status`, { status: newStatus });
      if (data.success) {
        toast.success(`User status updated to ${newStatus}`);
        fetchUsers();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Are you sure you want to delete this IT Support person? This action cannot be undone.');
    if (!confirmed) return;

    try {
      const { data } = await api.delete(`/users/${id}`);
      if (data.success) {
        toast.success('IT Support person deleted successfully');
        fetchUsers();
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete IT support person');
    }
  };

  const handleExport = () => {
    const exportData = users.map(u => ({
      'User ID': sanitizeInput(u.userId || 'N/A'),
      'Full Name': sanitizeInput(u.fullName || 'N/A'),
      'Email': sanitizeInput(u.email),
      'Mobile': sanitizeInput(u.mobile),
      'Status': sanitizeInput(u.status),
      'Joined': new Date(u.createdAt).toLocaleDateString()
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'IT Support');
    XLSX.writeFile(wb, `it_support_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(`Exported ${exportData.length} user(s)`);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (!formData.fullName || !formData.email || !formData.mobile) {
      toast.error('Please fill in all required fields');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      toast.error('Please enter a valid email address');
      return false;
    }
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(formData.mobile.trim())) {
      toast.error('Mobile number must be a valid 10-digit number');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSaving(true);
      const { confirmPassword, ...submitData } = formData;
      const { data } = await api.post('/users', submitData);
      if (data.success) {
        toast.success('IT Support person added successfully');
        setIsModalOpen(false);
        setFormData({
          fullName: '',
          email: '',
          mobile: '',
          password: '',
          confirmPassword: '',
          role: 'support',
          status: 'active',
          qualification: '',
          specialization: '',
          profileImage: ''
        });
        fetchUsers();
      }
    } catch (error) {
      console.error('Error adding IT support person:', error);
      toast.error(error.response?.data?.message || 'Failed to add IT support person');
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditFormData({
      fullName: user.fullName || '',
      email: user.email || '',
      mobile: user.mobile || '',
      password: '',
      confirmPassword: '',
      status: user.status || 'active',
      qualification: user.qualification || '',
      specialization: user.specialization || '',
      profileImage: user.profileImage || ''
    });
    setIsEditModalOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData.fullName || !editFormData.email || !editFormData.mobile) {
      toast.error('Please fill in all required fields');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editFormData.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(editFormData.mobile.trim())) {
      toast.error('Mobile number must be a valid 10-digit number');
      return;
    }
    if (editFormData.password) {
      if (editFormData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      if (editFormData.password !== editFormData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
    }

    try {
      setSaving(true);
      const submitData = {
        fullName: editFormData.fullName,
        email: editFormData.email,
        mobile: editFormData.mobile,
        status: editFormData.status,
        qualification: editFormData.qualification,
        specialization: editFormData.specialization,
        profileImage: editFormData.profileImage
      };
      if (editFormData.password) {
        submitData.password = editFormData.password;
      }

      const { data } = await api.put(`/users/${editingUser._id}`, submitData);
      if (data.success) {
        toast.success('IT Support person updated successfully');
        setIsEditModalOpen(false);
        setEditingUser(null);
        fetchUsers();
      }
    } catch (error) {
      console.error('Error updating IT support person:', error);
      toast.error(error.response?.data?.message || 'Failed to update IT support person');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { variant: 'success', icon: CheckCircle },
      inactive: { variant: 'default', icon: XCircle },
      suspended: { variant: 'danger', icon: AlertCircle }
    };
    const { variant, icon: Icon } = config[status] || config.inactive;

    return (
      <Badge variant={variant}>
        <Icon className="w-3 h-3 mr-1" />
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'N/A'}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50/50 dark:bg-[#00152E]/5 p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-[#002147] rounded-2xl shadow-sm border border-gray-200 dark:border-[#1a3884]/35 p-4 mb-6 animate-fade-in">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-white hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884] rounded-xl transition-all border border-gray-200 dark:border-[#1a3884]/45 group shadow-sm flex items-center justify-center shrink-0"
              title="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
            <div className="p-2 bg-[#1a3884]/10 dark:bg-[#1a3884]/20 rounded-xl shrink-0">
              <LifeBuoy className="w-6 h-6 text-[#1a3884] dark:text-[#4f7dff]" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#1a3884] dark:text-[#4f7dff] mb-1">
                IT Support Management
              </h1>
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <span className="font-medium">Manage IT support personnel and their access</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              icon={RefreshCw}
              onClick={fetchUsers}
              size="sm"
              className="px-3 py-1.5 text-[11px] h-auto border-gray-200/50 hover:border-[#1a3884]/50"
            >
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              variant="outline"
              icon={Download}
              onClick={handleExport}
              size="sm"
              className="px-3 py-1.5 text-[11px] h-auto border-gray-200/50 hover:border-[#1a3884]/50"
            >
              <span className="hidden sm:inline">Export</span>
            </Button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 text-sm px-4 py-2 bg-[#1a3884] hover:bg-[#00152E] text-white font-semibold rounded-xl transition-all shadow-md shadow-[#1a3884]/15 hover:shadow-lg hover:shadow-[#1a3884]/20 transform hover:-translate-y-0.5 transition-colors"
            >
              <Plus size={14} />
              <span className="hidden sm:inline">Add IT Support Person</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tab Buttons */}
      <div className="flex border-b border-gray-200 dark:border-[#1a3884]/30 mb-2">
        <button
          onClick={() => setActiveTab('management')}
          className={`px-6 py-3 font-semibold text-sm transition-all border-b-2 ${
            activeTab === 'management'
              ? 'border-[#1a3884] text-[#1a3884] dark:text-blue-400 dark:border-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          IT Support Management
        </button>
        {currentUser?.role !== 'office_admin' && (
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-6 py-3 font-semibold text-sm transition-all border-b-2 ${
              activeTab === 'analytics'
                ? 'border-[#1a3884] text-[#1a3884] dark:text-blue-400 dark:border-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            IT Support Analytics
          </button>
        )}
      </div>

      {activeTab === 'management' && (
        <>
          {/* Filters */}
      <div className="bg-white dark:bg-[#002147] rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-[#1a3884]/35">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Input
              icon={Search}
              placeholder="Search by name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-white/50 dark:bg-[#002A5C]/40 border-gray-200/50 dark:border-[#1a3884]/45 focus:bg-white dark:focus:bg-[#002A5C]/60 transition-all"
            />
          </div>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'suspended', label: 'Suspended' }
            ]}
            placeholder="All Status"
            className="bg-white/50 dark:bg-[#002A5C]/40 border-gray-200/50 dark:border-[#1a3884]/45"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#002147] rounded-2xl shadow-sm border border-gray-200 dark:border-[#1a3884]/35 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[850px]">
            <thead className="bg-gray-50/50 dark:bg-[#001f40]/80 border-b border-gray-200/50 dark:border-[#1a3884]/20">
              <tr>
                {['fullName', 'Contact', 'status', 'createdAt', 'lastLogin'].map(col => {
                  const sortable = col !== 'Contact';
                  return (
                    <th key={col}
                      className={`px-6 py-4 text-left text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider transition-colors ${
                        sortable ? 'cursor-pointer hover:bg-gray-100/50 dark:hover:bg-[#001f40]/50' : ''
                      }`}
                      onClick={() => sortable && handleSort(col)}
                    >
                      <div className="flex items-center gap-2">
                        {col === 'createdAt' ? 'Joined' : col === 'fullName' ? 'Person Details' : col.charAt(0).toUpperCase() + col.slice(1)}
                        {sortBy === col && <ArrowUpDown className="w-3 h-3 text-[#1a3884]" />}
                      </div>
                    </th>
                  );
                })}
                <th className="px-6 py-4 text-right text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200/50 dark:divide-[#1a3884]/20">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1a3884] animate-pulse"></div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Loading records...</p>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center">
                      <div className="p-4 bg-gray-50 dark:bg-[#001f40]/60 rounded-full mb-4">
                        <Users className="w-12 h-12 text-gray-400 dark:text-gray-300" />
                      </div>
                      <p className="text-gray-900 dark:text-gray-100 font-bold text-lg mb-1">No IT Support Found</p>
                      <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">Try adjusting your search or filters</p>
                      <Button variant="outline" onClick={() => setSearchTerm('')}>Clear Search</Button>
                    </div>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr 
                    key={user._id} 
                    className="hover:bg-[#00152E]/5 dark:hover:bg-[#001f40]/80 transition-colors border-b border-gray-100/50 dark:border-[#1a3884]/15 group"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-[#00152E] to-[#1a3884] rounded-full flex items-center justify-center shadow-sm overflow-hidden group-hover:scale-110 transition-transform">
                          {user.profileImage ? (
                            <img src={getFileUrl(user.profileImage)} className="w-full h-full object-cover" alt="Profile" />
                          ) : (
                            <span className="text-white font-bold text-sm">
                              {sanitizeInput(user.fullName?.charAt(0)?.toUpperCase() || 'U')}
                            </span>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-bold text-gray-900 dark:text-gray-100">{sanitizeInput(user.fullName || 'N/A')}</div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">{sanitizeInput(user.userId || 'N/A')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col space-y-1.5">
                        <div className="flex items-center text-[12px] text-gray-600 dark:text-gray-300 font-medium">
                          <Mail className="w-3.5 h-3.5 mr-2 text-gray-400 dark:text-gray-300" />
                          {sanitizeInput(user.email)}
                        </div>
                        <div className="flex items-center text-[12px] text-gray-600 dark:text-gray-300 font-medium">
                          <Phone className="w-3.5 h-3.5 mr-2 text-gray-400 dark:text-gray-300" />
                          {sanitizeInput(user.mobile)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(user.status)}
                    </td>
                    <td className="px-6 py-4 text-[12px] text-gray-600 dark:text-gray-300 font-medium">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-[12px] text-gray-600 dark:text-gray-300 font-medium">
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 text-right relative">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="px-3 py-1.5 text-[11px] h-auto border-gray-200/50 hover:border-[#1a3884]/50"
                          onClick={() => navigate(`/users/${user._id}`)}
                        >
                          View Details
                        </Button>
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdown(openDropdown === user._id ? null : user._id);
                            }}
                            className="p-2 text-gray-400 hover:text-[#1a3884] rounded-lg hover:bg-[#00152E]/5 dark:hover:bg-[#001f40]/55 transition-all"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {openDropdown === user._id && (
                            <div className="absolute right-0 bottom-full mb-2 w-48 bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/45 rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.3)] z-[100] py-1.5 overflow-visible">
                              <button
                                onClick={() => {
                                  handleEditClick(user);
                                  setOpenDropdown(null);
                                }}
                                className="w-full px-4 py-2 text-left text-[12px] text-gray-700 dark:text-gray-200 hover:bg-[#00152E]/5 dark:hover:bg-[#001f40]/80 hover:text-[#1a3884] dark:hover:text-[#4f7dff] flex items-center transition-colors"
                              >
                                <Edit className="w-3.5 h-3.5 mr-2.5" />
                                Edit Profile
                              </button>
                              <button
                                onClick={() => {
                                  handleStatusChange(user._id, user.status === 'active' ? 'inactive' : 'active');
                                  setOpenDropdown(null);
                                }}
                                className={`w-full px-4 py-2 text-left text-[12px] flex items-center transition-colors ${user.status === 'active' ? 'text-amber-600 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'
                                  }`}
                              >
                                {user.status === 'active' ? (
                                  <>
                                    <UserX className="w-3.5 h-3.5 mr-2.5" />
                                    Deactivate Access
                                  </>
                                ) : (
                                  <>
                                    <UserCheck className="w-3.5 h-3.5 mr-2.5" />
                                    Activate Access
                                  </>
                                )}
                              </button>

                              <div className="my-1 border-t border-gray-100 dark:border-[#1a3884]/30"></div>
                              <button
                                onClick={() => {
                                  handleDelete(user._id);
                                  setOpenDropdown(null);
                                }}
                                className="w-full px-4 py-2 text-left text-[12px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-2.5" />
                                Delete Person
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-4 bg-gray-50/50 dark:bg-[#001f40]/40 border-t border-gray-200/50 dark:border-[#1a3884]/20 flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="text-[12px] font-medium text-gray-500 dark:text-gray-400 text-center sm:text-left">
              Showing page <span className="text-gray-900 dark:text-white font-semibold">{currentPage}</span> of <span className="text-gray-900 dark:text-white font-semibold">{totalPages}</span>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-end">
              <Button
                onClick={() => setCurrentPage(c => c - 1)}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
                className="flex-1 sm:flex-initial h-8 px-4 justify-center"
              >
                Previous
              </Button>
              <Button
                onClick={() => setCurrentPage(c => c + 1)}
                disabled={currentPage === totalPages}
                variant="outline"
                size="sm"
                className="flex-1 sm:flex-initial h-8 px-4 justify-center"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
      </>
      )}

      {activeTab === 'analytics' && currentUser?.role !== 'office_admin' && (
        <div className="space-y-6 animate-fade-in">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Card 1: Total Inquiries */}
            <div className="bg-white dark:bg-[#002147] p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-[#1a3884]/35 relative overflow-hidden flex items-center gap-5">
              <div className="absolute -right-4 -top-4 w-24 h-24 bg-blue-50/40 dark:bg-blue-900/10 rounded-full"></div>
              <div className="relative p-4 bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 rounded-2xl shrink-0">
                <MessageSquare size={28} />
              </div>
              <div className="relative">
                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-300 uppercase tracking-widest">Total Inquiries</p>
                <h3 className="text-3xl font-black text-[#00152E] dark:text-white mt-1">
                  {loadingTickets ? '...' : allTickets.length}
                </h3>
                <p className="text-[11px] text-blue-500 mt-1 font-bold">All-time raised tickets</p>
              </div>
            </div>

            {/* Card 2: Awaiting Action */}
            <div className="bg-white dark:bg-[#002147] p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-[#1a3884]/35 relative overflow-hidden flex items-center gap-5">
              <div className="absolute -right-4 -top-4 w-24 h-24 bg-amber-50/40 dark:bg-amber-900/10 rounded-full"></div>
              <div className="relative p-4 bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400 rounded-2xl shrink-0">
                <Clock size={28} />
              </div>
              <div className="relative">
                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-300 uppercase tracking-widest">Awaiting Action</p>
                <h3 className="text-3xl font-black text-[#00152E] dark:text-white mt-1">
                  {loadingTickets ? '...' : allTickets.filter(t => t.status === 'open' || t.status === 'in-progress').length}
                </h3>
                <p className="text-[11px] text-amber-600 mt-1 font-bold">Requires attention</p>
              </div>
            </div>

            {/* Card 3: Successfully Resolved */}
            <div className="bg-white dark:bg-[#002147] p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-[#1a3884]/35 relative overflow-hidden flex items-center gap-5">
              <div className="absolute -right-4 -top-4 w-24 h-24 bg-emerald-50/40 dark:bg-emerald-900/10 rounded-full"></div>
              <div className="relative p-4 bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 rounded-2xl shrink-0">
                <CheckCircle size={28} />
              </div>
              <div className="relative">
                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-300 uppercase tracking-widest">Successfully Resolved</p>
                <h3 className="text-3xl font-black text-[#00152E] dark:text-white mt-1">
                  {loadingTickets ? '...' : allTickets.filter(t => t.status === 'resolved' || t.status === 'closed').length}
                </h3>
                <p className="text-[11px] text-emerald-600 mt-1 font-bold">Completed tasks</p>
              </div>
            </div>
          </div>

          {/* Ticket Categories Chart */}
          <div className="bg-white dark:bg-[#002147] rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-[#1a3884]/35">
            <h2 className="text-lg font-bold text-[#00152E] dark:text-white mb-6 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#1a3884] dark:text-blue-400" />
              Inquiries by Category
            </h2>
            
            {loadingTickets ? (
              <div className="py-12 flex flex-col items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a3884] dark:border-blue-400"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Loading chart data...</p>
              </div>
            ) : allTickets.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                No tickets available to display chart.
              </div>
            ) : (
              <div className="h-80 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={getCategoryStats()}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '0.75rem',
                        fontSize: '12px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                      labelStyle={{ fontWeight: 'bold', color: '#0f172a' }}
                      cursor={{ fill: '#f8fafc' }}
                    />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                      {getCategoryStats().map((entry, index) => {
                        const colors = [
                          '#60a5fa', // Technical - soft blue
                          '#34d399', // Account - soft emerald
                          '#fbbf24', // Course - soft amber
                          '#a78bfa', // Assessment - soft violet
                          '#f87171', // Career Direction - soft red
                          '#38bdf8', // Placement - soft sky
                          '#fb7185', // Certificates & Badges - soft rose
                          '#a3e635'  // Other - soft lime
                        ];
                        return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Breakdown Section */}
          <div className="bg-white dark:bg-[#002147] rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-[#1a3884]/35">
            <h2 className="text-lg font-bold text-[#00152E] dark:text-white mb-6 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#1a3884] dark:text-blue-400" />
              IT Support Personnel Performance
            </h2>

            {loadingTickets ? (
              <div className="py-12 flex flex-col items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a3884] dark:border-blue-400"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Calculating analytics...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                No IT support personnel registered.
              </div>
            ) : (
              <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-200/70 dark:border-[#1a3884]/35 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-gray-50/50 dark:bg-[#001f40]/80 border-b border-gray-200/50 dark:border-[#1a3884]/20">
                      <tr>
                        <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Support Person</th>
                        <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Total Tickets</th>
                        <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Pending Tickets</th>
                        <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Solved Tickets</th>
                        <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Resolution Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200/50 dark:divide-[#1a3884]/20">
                      {users.map(person => {
                        const stats = getPersonStats(person._id);
                        const resolutionRate = stats.total > 0 ? Math.round((stats.resolved / stats.total) * 100) : 0;
                        return (
                          <tr 
                            key={person._id} 
                            className="hover:bg-[#00152E]/5 dark:hover:bg-[#001f40]/80 transition-colors border-b border-gray-100/50 dark:border-[#1a3884]/15"
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-[#00152E] to-[#1a3884] rounded-full flex items-center justify-center shadow-sm overflow-hidden text-white font-bold">
                                  {person.profileImage ? (
                                    <img src={getFileUrl(person.profileImage)} className="w-full h-full object-cover" alt="Profile" />
                                  ) : (
                                    sanitizeInput(person.fullName?.charAt(0)?.toUpperCase() || 'U')
                                  )}
                                </div>
                                <div className="ml-4 min-w-0">
                                  <div className="text-sm font-bold text-gray-900 dark:text-gray-100">{sanitizeInput(person.fullName || 'N/A')}</div>
                                  <div className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">{sanitizeInput(person.email)}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm font-bold text-gray-800 dark:text-gray-200">
                              {stats.total}
                            </td>
                            <td className="px-6 py-4 text-sm font-bold text-amber-600">
                              {stats.awaiting}
                            </td>
                            <td className="px-6 py-4 text-sm font-bold text-emerald-600">
                              {stats.resolved}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-24 h-1.5 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden shrink-0">
                                  <div
                                    className="h-full bg-gradient-to-r from-[#1a3884] to-[#4f7dff] rounded-full transition-all duration-500"
                                    style={{ width: `${resolutionRate}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{resolutionRate}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add IT Support Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#00152E] rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100 dark:border-[#1a3884]/40 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-[#1a3884]/40">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-white hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884]/50 rounded-xl transition-all border border-gray-200 dark:border-[#1a3884]/45 group shadow-sm flex items-center justify-center shrink-0 mr-1"
                  title="Back"
                >
                  <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
                </button>
                <div className="p-2 bg-[#00152E]/5 dark:bg-[#1a3884]/20 rounded-lg">
                  <Plus className="w-6 h-6 text-[#1a3884] dark:text-[#4f7dff]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add IT Support Person</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Onboard a new IT support team member</p>
                </div>
              </div>
              <button type="button" onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors text-gray-400 dark:text-gray-400 hidden sm:block">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col max-h-[85vh]">
              <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(85vh-150px)] custom-scrollbar">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Full Name *</label>
                    <Input
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter full name"
                      required
                      className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                      required
                      autoComplete="off"
                      className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Mobile Number *</label>
                    <Input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="10-digit number"
                      required
                      autoComplete="off"
                      className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Password *</label>
                    <Input
                      type={showAddPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="At least 6 characters"
                      required
                      autoComplete="new-password"
                      className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                      icon={showAddPassword ? EyeOff : Eye}
                      onIconClick={() => setShowAddPassword(!showAddPassword)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Confirm Password *</label>
                    <Input
                      type={showAddConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Re-enter password"
                      required
                      autoComplete="new-password"
                      className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                      icon={showAddConfirmPassword ? EyeOff : Eye}
                      onIconClick={() => setShowAddConfirmPassword(!showAddConfirmPassword)}
                    />
                  </div>

                  <div className="md:col-span-2 grid grid-cols-2 gap-5 pt-2 border-t border-gray-50 dark:border-[#1a3884]/20 mt-2">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Qualification</label>
                      <Input
                        name="qualification"
                        value={formData.qualification}
                        onChange={handleChange}
                        placeholder="e.g. B.Tech"
                        className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Specialization</label>
                      <Input
                        name="specialization"
                        value={formData.specialization}
                        onChange={handleChange}
                        placeholder="e.g. Networking"
                        className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <FileUpload
                      type="image"
                      label="Profile Image"
                      onUploadComplete={(fileData) => setFormData(prev => ({ ...prev, profileImage: fileData.url }))}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 items-center justify-end p-6 border-t border-gray-100 dark:border-[#1a3884]/40 bg-gray-50/50 dark:bg-[#001f40]/40">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="w-full sm:w-auto px-6 h-11 border-gray-200 hover:bg-gray-50 dark:border-[#1a3884]/50 dark:hover:bg-[#1a3884]/30 dark:text-gray-300 dark:bg-transparent justify-center"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="px-8 h-11 bg-[#1a3884] hover:bg-[#00152E] text-white shadow-lg shadow-[#1a3884]/20 dark:shadow-blue-900/20 border-none rounded-xl transition-all font-semibold"
                >
                  {saving ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Add Person
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit IT Support Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#00152E] rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100 dark:border-[#1a3884]/40 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-[#1a3884]/40">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-white hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884]/50 rounded-xl transition-all border border-gray-200 dark:border-[#1a3884]/45 group shadow-sm flex items-center justify-center shrink-0 mr-1"
                  title="Back"
                >
                  <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
                </button>
                <div className="p-2 bg-[#00152E]/5 dark:bg-[#1a3884]/20 rounded-lg">
                  <Edit className="w-6 h-6 text-[#1a3884] dark:text-[#4f7dff]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit IT Support Person</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Update details for the IT support team member</p>
                </div>
              </div>
              <button type="button" onClick={() => setIsEditModalOpen(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors text-gray-400 dark:text-gray-400 hidden sm:block">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="flex flex-col max-h-[85vh]">
              <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(85vh-150px)] custom-scrollbar">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Full Name *</label>
                    <Input
                      name="fullName"
                      value={editFormData.fullName}
                      onChange={handleEditChange}
                      placeholder="Enter full name"
                      required
                      className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={editFormData.email}
                      onChange={handleEditChange}
                      placeholder="email@example.com"
                      required
                      autoComplete="off"
                      className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Mobile Number *</label>
                    <Input
                      type="tel"
                      name="mobile"
                      value={editFormData.mobile}
                      onChange={handleEditChange}
                      placeholder="10-digit number"
                      required
                      autoComplete="off"
                      className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Password</label>
                    <Input
                      type={showEditPassword ? "text" : "password"}
                      name="password"
                      value={editFormData.password}
                      onChange={handleEditChange}
                      placeholder="Leave blank to keep unchanged"
                      autoComplete="new-password"
                      className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                      icon={showEditPassword ? EyeOff : Eye}
                      onIconClick={() => setShowEditPassword(!showEditPassword)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Confirm Password</label>
                    <Input
                      type={showEditConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={editFormData.confirmPassword}
                      onChange={handleEditChange}
                      placeholder="Leave blank to keep unchanged"
                      autoComplete="new-password"
                      className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                      icon={showEditConfirmPassword ? EyeOff : Eye}
                      onIconClick={() => setShowEditConfirmPassword(!showEditConfirmPassword)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Status *</label>
                    <Select
                      name="status"
                      value={editFormData.status}
                      onChange={handleEditChange}
                      options={[
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' },
                        { value: 'suspended', label: 'Suspended' }
                      ]}
                      required
                      className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                    />
                  </div>

                  <div className="md:col-span-2 grid grid-cols-2 gap-5 pt-2 border-t border-gray-50 dark:border-[#1a3884]/20 mt-2">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Qualification</label>
                      <Input
                        name="qualification"
                        value={editFormData.qualification}
                        onChange={handleEditChange}
                        placeholder="e.g. B.Tech"
                        className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">Specialization</label>
                      <Input
                        name="specialization"
                        value={editFormData.specialization}
                        onChange={handleEditChange}
                        placeholder="e.g. Networking"
                        className="h-11 dark:bg-[#002147] dark:border-[#1a3884]/50 dark:text-white"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <FileUpload
                      type="image"
                      label="Profile Image"
                      onUploadComplete={(fileData) => setEditFormData(prev => ({ ...prev, profileImage: fileData.url }))}
                    />
                    {editFormData.profileImage && (
                      <div className="mt-2 flex items-center gap-3">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Current Image:</span>
                        <img 
                          src={getFileUrl(editFormData.profileImage)} 
                          alt="Current Profile" 
                          className="w-10 h-10 object-cover rounded-full border border-gray-200 dark:border-[#1a3884]/40" 
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 items-center justify-end p-6 border-t border-gray-100 dark:border-[#1a3884]/40 bg-gray-50/50 dark:bg-[#001f40]/40">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditModalOpen(false)}
                  className="w-full sm:w-auto px-6 h-11 border-gray-200 hover:bg-gray-50 dark:border-[#1a3884]/50 dark:hover:bg-[#1a3884]/30 dark:text-gray-300 dark:bg-transparent justify-center"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="px-8 h-11 bg-[#1a3884] hover:bg-[#00152E] text-white shadow-lg shadow-[#1a3884]/20 dark:shadow-blue-900/20 border-none rounded-xl transition-all font-semibold"
                >
                  {saving ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ITSupportManagement;
